import {
  type ColumnsType,
  ConfirmModal,
  Loading,
  Table,
  TableRowMenuCell,
  ToggleAllButton,
} from '@/components';
import { useMasterTablePermission } from '@/features/authentication/_hooks/permission';
import { useExpandedRowKeys } from '@/hooks';
import { useMessage } from '@/providers';
import {
  NEST_ITEM_CONNECTOR,
  TABLE_CODE_WIDTH,
  TABLE_DATETIME_WIDTH,
  TABLE_ROW_MENU_WIDTH,
  convertUpperToArray,
  datetimeFormat,
  findUpperRecursive,
} from '@/utils';
import dayjs from 'dayjs';
import { groupBy } from 'lodash-es';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import {
  useCreateExpenditurePurposeOfSettlementStatisticsPreset,
  useDeleteExpenditurePurposeOfSettlementStatisticsPreset,
  useExpenditurePurposeOfSettlementStatisticsPresets,
  useExpenditurePurposes,
  useExpenditurePurposesOfSettlementStatistics,
  useUpdateExpenditurePurposeOfSettlementStatisticsPreset,
} from '../../hooks';
import { AccountingTypeLabelModifier } from '../../models';
import { DEFAULT_PAGINATION_SETTING, fillZeroToAccountType } from '../../utils';
import { useDrawerState } from '../DrawerStateProvider';
import { ExpenditurePurposeOfSettlementStatisticsPresetFormDrawer } from './ExpenditurePurposeOfSettlementStatisticsPresetFormDrawer';

export const ExpenditurePurposeOfSettlementStatisticsPresetsPage: FC = () => {
  const { t } = useTranslation();
  const { drawerState, setDrawerState } = useDrawerState();
  const { openMessage } = useMessage();
  const expenditurePurposeOfSettlementStatisticsPresets =
    useExpenditurePurposeOfSettlementStatisticsPresets();
  const expenditurePurposes = useExpenditurePurposes();
  const expenditurePurposesOfSettlementStatistics =
    useExpenditurePurposesOfSettlementStatistics();
  const { mutateAsync: createAsync } =
    useCreateExpenditurePurposeOfSettlementStatisticsPreset();
  const { mutateAsync: updateAsync } =
    useUpdateExpenditurePurposeOfSettlementStatisticsPreset();
  const { mutateAsync: deleteAsync } =
    useDeleteExpenditurePurposeOfSettlementStatisticsPreset();
  const editPermission = useMasterTablePermission(true);
  const {
    expandedRowKeys,
    addExpandedRowKeys,
    setExpandedRowKeys,
    onExpand,
    clearExpandedRowKeys,
  } = useExpandedRowKeys();
  const [expenditurePurposeDelete, setExpenditurePurposeDelete] = useState<
    string | null
  >(null);

  if (
    expenditurePurposeOfSettlementStatisticsPresets.isLoading ||
    !expenditurePurposeOfSettlementStatisticsPresets.data
  ) {
    return <Loading />;
  }

  const groupedExpenditurePurposeOfSettlementStatisticsPresets = Object.entries(
    groupBy(
      expenditurePurposeOfSettlementStatisticsPresets.data.map((d) => ({
        ...d,
        expenditurePurposes: convertUpperToArray(d.expenditurePurpose),
      })),
      'accountingId',
    ),
  ).flatMap(([accountingId, data]) => {
    const accounting = data.find(
      (d) => d.accountingId === accountingId,
    )?.accounting;
    if (!accounting) {
      return [];
    }
    return {
      id: accountingId,
      type: accounting.type,
      name: accounting.name,
      code: accounting.code,
      // TODO: make ExpenditurePurpose recursive if we can.
      data,
    };
  });

  const selected = expenditurePurposeOfSettlementStatisticsPresets.data.find(
    (d) =>
      drawerState.open &&
      drawerState.type ===
        'expenditure-purpose-of-settlement-statistics-preset' &&
      drawerState.isEditing &&
      drawerState.id === d.id,
  );

  const openAllExpanded = () => {
    setExpandedRowKeys(
      groupedExpenditurePurposeOfSettlementStatisticsPresets.map((v) => v.id),
    );
  };

  const handleCloseDrawer = async () => {
    setDrawerState({ open: false });
  };

  return (
    <>
      <Table
        enableSticky={false}
        dataSource={groupedExpenditurePurposeOfSettlementStatisticsPresets}
        rowKey="id"
        title={() => (
          <ToggleAllButton
            isExpandedAllWhenMounted
            openAll={openAllExpanded}
            closeAll={clearExpandedRowKeys}
          />
        )}
        columns={[
          {
            title: t('code'),
            key: 'code',
            dataIndex: 'code',
            width: TABLE_CODE_WIDTH,
            render: fillZeroToAccountType,
          },
          {
            title: t('accounting-type'),
            key: 'type',
            dataIndex: 'type',
            width: 120,
            render: (value) => AccountingTypeLabelModifier[value],
          },
          {
            title: t('accounting-name'),
            key: 'name',
            dataIndex: 'name',
          },
        ]}
        expandable={{
          expandedRowKeys,
          onExpand: (expanded, record) => {
            onExpand(expanded, record.id);
          },
          expandedRowRender: (accountingGroups) => (
            <Table
              enableSticky={false}
              dataSource={accountingGroups.data}
              rowKey="id"
              columns={[
                {
                  title: t('expenditure-purpose'),
                  key: 'expenditurePurposeId',
                  dataIndex: 'expenditurePurposeId',
                  render: (value) => (
                    <>
                      {findUpperRecursive(
                        value,
                        expenditurePurposes.data ?? [],
                        [],
                      )
                        .map((d) => d.name)
                        .reverse()
                        .join(NEST_ITEM_CONNECTOR)}
                    </>
                  ),
                },
                {
                  title: t('expenditure-purpose-of-settlement-statistics'),
                  key: 'expenditurePurposeOfSettlementStatisticsId',
                  dataIndex: 'expenditurePurposeOfSettlementStatisticsId',
                  render: (value) => (
                    <>
                      {findUpperRecursive(
                        value,
                        expenditurePurposesOfSettlementStatistics.data ?? [],
                        [],
                      )
                        .map((d) => d.name)
                        .reverse()
                        .join(NEST_ITEM_CONNECTOR)}
                    </>
                  ),
                },
                {
                  title: t('created-datetime'),
                  key: 'createdAt',
                  dataIndex: 'createdAt',
                  width: TABLE_DATETIME_WIDTH,
                  render: (value) => datetimeFormat({ datetime: value }),
                },
                {
                  title: t('updated-datetime'),
                  key: 'updatedAt',
                  dataIndex: 'updatedAt',
                  width: TABLE_DATETIME_WIDTH,
                  render: (value) => datetimeFormat({ datetime: value }),
                },
                ...(editPermission.data
                  ? ([
                      {
                        title: '',
                        key: 'actions',
                        fixed: 'right' as const,
                        width: TABLE_ROW_MENU_WIDTH,
                        onCell: () => ({
                          style: { minWidth: TABLE_ROW_MENU_WIDTH },
                        }),
                        render: (_, record) => (
                          <TableRowMenuCell
                            onEdit={() => {
                              setDrawerState({
                                open: true,
                                type: 'expenditure-purpose-of-settlement-statistics-preset',
                                isEditing: true,
                                id: record.id,
                              });
                            }}
                            items={[
                              {
                                key: 'delete',
                                label: t('delete'),
                                danger: true,
                                onClick: () =>
                                  setExpenditurePurposeDelete(record.id),
                              },
                            ]}
                          />
                        ),
                      },
                    ] satisfies ColumnsType<
                      (typeof accountingGroups.data)[number]
                    >)
                  : []),
              ]}
              pagination={false}
            />
          ),
        }}
        pagination={DEFAULT_PAGINATION_SETTING}
      />

      <ExpenditurePurposeOfSettlementStatisticsPresetFormDrawer
        {...(drawerState.open && drawerState.isEditing
          ? {
              type: 'edit',
              initialValues: {
                accountingId: selected?.accountingId,
                expenditurePurposeIds: selected
                  ? findUpperRecursive(
                      selected.expenditurePurposeId,
                      expenditurePurposes.data ?? [],
                      [],
                    )
                      .map((d) => d.id)
                      .reverse()
                  : [],
                expenditurePurposeOfSettlementStatisticsIds: selected
                  ? findUpperRecursive(
                      selected.expenditurePurposeOfSettlementStatisticsId,
                      expenditurePurposesOfSettlementStatistics.data ?? [],
                    )
                      .map((d) => d.id)
                      .reverse()
                  : [],
                period: [
                  selected?.startYear
                    ? dayjs().year(selected.startYear)
                    : undefined,
                  selected?.endYear
                    ? dayjs().year(selected.endYear)
                    : undefined,
                ],
              },
            }
          : { type: 'new' })}
        onSubmit={async (values) => {
          invariant(drawerState.open, t('unreachable-code'));

          const res = drawerState.isEditing
            ? await updateAsync({ ...values, id: drawerState.id })
            : await createAsync(values);

          await Promise.all([
            openMessage(
              'success',
              drawerState.isEditing
                ? t('message-update-completed')
                : t('message-create-completed'),
            ),
            handleCloseDrawer(),
            !drawerState.isEditing &&
              (async () => {
                addExpandedRowKeys([res.accountingId]);
              })(),
          ]);
        }}
      />
      <ConfirmModal
        open={expenditurePurposeDelete !== null}
        title={t('delete-confirmation')}
        disclaimerText={t('delete-confirmation-disclaimer')}
        onOk={async () => {
          invariant(expenditurePurposeDelete, t('unreachable-code'));
          try {
            await deleteAsync(expenditurePurposeDelete);
            await Promise.all([
              openMessage('success', t('message-delete-completed')),
              (async () => {
                setExpenditurePurposeDelete(null);
              })(),
            ]);
          } catch {
            await openMessage('error', t('message-delete-failed'));
          }
        }}
        onCancel={() => setExpenditurePurposeDelete(null)}
        skipCheck
      />
    </>
  );
};
